/**
 * Preset Test Maps
 * Hand-designed maps for testing specific visibility and movement features.
 */

import type { GridMap, Trigger } from '../types';
import { createGrid, carveRoom, carveCorridor, setWall, addEntity, setTerrain } from '../grid';

/** Small room — tests basic visibility in a confined space. */
function presetSmallRoom(): GridMap {
  const map = createGrid(15, 15, 'wall');
  map.name = 'Small Room';

  // Central 7x7 room
  carveRoom(map, 4, 4, 7, 7);

  // Door-like openings (corridors leading out)
  carveCorridor(map, 7, 1, 7, 4, false);  // North exit
  carveCorridor(map, 7, 10, 7, 13, false); // South exit

  return addEntity(map, {
    id: 'player', x: 7, y: 7, type: 'player', name: 'Player', lightRadius: 10,
  });
}

/** Long corridor with side rooms — tests corridor visibility. */
function presetCorridor(): GridMap {
  const map = createGrid(40, 15, 'wall');
  map.name = 'Corridor';

  // Main horizontal corridor
  carveRoom(map, 1, 6, 38, 3);

  // Side rooms at intervals
  carveRoom(map, 5, 1, 5, 5);    // Top room 1
  carveRoom(map, 15, 1, 5, 5);   // Top room 2
  carveRoom(map, 25, 1, 5, 5);   // Top room 3
  carveRoom(map, 10, 9, 5, 5);   // Bottom room 1
  carveRoom(map, 20, 9, 5, 5);   // Bottom room 2
  carveRoom(map, 30, 9, 5, 5);   // Bottom room 3

  return addEntity(map, {
    id: 'player', x: 3, y: 7, type: 'player', name: 'Player', lightRadius: 12,
  });
}

/** Pillar hall — tests shadowcasting around evenly spaced obstacles. */
function presetPillarHall(): GridMap {
  const map = createGrid(25, 25, 'wall');
  map.name = 'Pillar Hall';

  // Large open room
  carveRoom(map, 1, 1, 23, 23);

  // Grid of pillars
  for (let y = 4; y < 22; y += 4) {
    for (let x = 4; x < 22; x += 4) {
      map.cells[y][x].terrain = 'wall';
    }
  }

  return addEntity(map, {
    id: 'player', x: 2, y: 2, type: 'player', name: 'Player', lightRadius: 15,
  });
}

/** Wall-edge test — tests edge walls that block movement but not sight. */
function presetWallEdges(): GridMap {
  const map = createGrid(20, 20, 'wall');
  map.name = 'Wall Edges';

  // Large open room
  carveRoom(map, 1, 1, 18, 18);

  // Add some edge walls (fences/railings that block movement)
  // Horizontal fence across the middle
  for (let x = 3; x < 17; x++) {
    setWall(map, x, 10, 'S', true);
  }
  // Gap in the fence
  setWall(map, 10, 10, 'S', false);

  // Vertical fence on the left
  for (let y = 3; y < 17; y++) {
    setWall(map, 6, y, 'E', true);
  }
  // Gap
  setWall(map, 6, 10, 'E', false);

  return addEntity(map, {
    id: 'player', x: 3, y: 3, type: 'player', name: 'Player', lightRadius: 15,
  });
}

/**
 * Trigger Demo — 25×25 map with 4 rooms demonstrating all trigger conditions.
 *
 * Layout:
 *   NW room (player start) — connected east and south
 *   NE room — step+dialogue on entry
 *   SW room — proximity warning + step trap (flag-chained)
 *   SE room — visible+spawn when first seen, interact+dialogue chest
 *   Central teleport stairs connecting NW and SE corridors
 */
function presetTriggerDemo(): GridMap {
  const map = createGrid(25, 25, 'wall');
  map.name = 'Trigger Demo';

  // ── Rooms ──
  carveRoom(map, 2, 2, 8, 8);     // NW room (player start)
  carveRoom(map, 15, 2, 8, 8);    // NE room
  carveRoom(map, 2, 15, 8, 8);    // SW room
  carveRoom(map, 15, 15, 8, 8);   // SE room

  // ── Corridors ──
  // NW → NE (horizontal, y=6)
  carveCorridor(map, 10, 6, 14, 6, true);
  // NW → SW (vertical, x=6)
  carveCorridor(map, 6, 10, 6, 14, false);
  // NE → SE (vertical, x=19)
  carveCorridor(map, 19, 10, 19, 14, false);
  // SW → SE (horizontal, y=19)
  carveCorridor(map, 10, 19, 14, 19, true);

  // ── Doors ──
  setTerrain(map, 10, 6, 'door');   // NW→NE corridor
  setTerrain(map, 6, 10, 'door');   // NW→SW corridor
  setTerrain(map, 19, 10, 'door');  // NE→SE corridor
  setTerrain(map, 10, 19, 'door');  // SW→SE corridor

  // ── Objects ──
  // Chest in SE room (interact target)
  let mapWithEntities = addEntity(map, {
    id: 'player', x: 5, y: 5, type: 'player', name: 'Player', lightRadius: 10,
  });
  mapWithEntities = addEntity(mapWithEntities, {
    id: 'chest', x: 19, y: 19, type: 'object', name: 'Chest', icon: '📦',
  });

  // Stairs markers (visual cue for teleport)
  setTerrain(map, 9, 5, 'stairs_down');  // NW corridor entrance
  setTerrain(map, 16, 20, 'stairs_up');  // SE room corner

  // ── Triggers ──
  const triggers: Trigger[] = [
    // 1. Step + dialogue: entering NE room triggers narration
    {
      id: 'ne_room_enter',
      cells: [{ x: 15, y: 6 }],
      condition: 'step',
      effect: {
        type: 'dialogue',
        text: 'You enter the eastern chamber. Ancient runes pulse faintly along the walls, casting shifting shadows across the stone floor.',
        speaker: 'Narrator',
      },
      visible: true,
      icon: '!',
      color: '#fbbf24',
    },

    // 2. Proximity + dialogue: warning before the trap in SW room
    {
      id: 'sw_trap_warning',
      cells: [{ x: 6, y: 19 }],
      condition: 'proximity',
      radius: 3,
      effect: {
        type: 'dialogue',
        text: 'You notice scratches on the floor stones ahead — a pressure plate, perhaps?',
        speaker: 'Narrator',
      },
      setsFlag: 'trap_warned',
    },

    // 3. Step + trap: pressure plate in SW room center (oneShot)
    {
      id: 'sw_pressure_plate',
      cells: [{ x: 6, y: 19 }],
      condition: 'step',
      effect: {
        type: 'trap',
        damage: 2,
        description: 'A hidden pressure plate clicks beneath your foot. Darts shoot from the walls!',
      },
      visible: true,
      icon: '⚠',
      color: '#ef4444',
    },

    // 4. Visible + spawn: monsters appear when SE room is first seen
    {
      id: 'se_ambush',
      cells: [{ x: 18, y: 18 }],
      condition: 'visible',
      effect: {
        type: 'spawn',
        entities: [
          { entityType: 'monster', name: 'Shadow Rat', x: 17, y: 17 },
          { entityType: 'monster', name: 'Shadow Rat', x: 20, y: 17 },
        ],
      },
    },

    // 5. Interact + dialogue: inspectable chest
    {
      id: 'se_chest_inspect',
      cells: [{ x: 19, y: 19 }],
      condition: 'interact',
      effect: {
        type: 'dialogue',
        text: 'The old chest creaks open. Inside you find a tarnished silver amulet bearing an unfamiliar sigil.',
        speaker: 'Narrator',
      },
      visible: true,
      icon: '?',
      color: '#34d399',
    },

    // 6. Step + teleport: stairs connecting NW corridor to SE room (repeatable)
    {
      id: 'stairs_nw_to_se',
      cells: [{ x: 9, y: 5 }],
      condition: 'step',
      effect: { type: 'teleport', targetX: 16, targetY: 20 },
      oneShot: false,
      visible: true,
      icon: '↓',
      color: '#60a5fa',
    },
    {
      id: 'stairs_se_to_nw',
      cells: [{ x: 16, y: 20 }],
      condition: 'step',
      effect: { type: 'teleport', targetX: 9, targetY: 5 },
      oneShot: false,
      visible: true,
      icon: '↑',
      color: '#60a5fa',
    },
  ];

  mapWithEntities.triggers = triggers;

  return mapWithEntities;
}

/** All presets indexed by name */
export const PRESETS: Record<string, () => GridMap> = {
  small_room: presetSmallRoom,
  corridor: presetCorridor,
  pillar_hall: presetPillarHall,
  wall_edges: presetWallEdges,
  trigger_demo: presetTriggerDemo,
};

export const PRESET_LABELS: Record<string, string> = {
  small_room: 'Small Room',
  corridor: 'Corridor',
  pillar_hall: 'Pillar Hall',
  wall_edges: 'Wall Edges',
  trigger_demo: 'Trigger Demo',
};
